import { noteNavigationTarget } from '../../utils/note_anchor';
import {
  narrationTopicsFor,
  narrationTopicsForConcepts,
  topicAtSecond,
} from '../../managers/document_proposal_topics';
import { logAutopilotPayload } from '../../utils/autopilot_debug';

angular.module("LeasePilot").directive("dealTermsImportCard", [
  "$rootScope",
  "ApiService",
  "DealTermsImportService",
  "FieldConstraintsService",
  "$interval",
  "AiServicesConsentService",
  function(
    $rootScope,
    ApiService,
    DealTermsImportService,
    FieldConstraintsService,
    $interval,
    AiServicesConsentService
  ) {
    return {
      restrict: "E",
      templateUrl: "deal_terms_import_card.html",
      scope: {
        lease: "="
      },
      link: function($scope, element, attrs) {
        // Track deal terms import state
        $scope.isDealTermsImportActive = false;
        $scope.documentProposalPhase = null;
        $scope.documentApplyPromise = null;
        $scope.documentProposalSourceText = null;
        $scope.documentProposalSourceDocument = null;
        $scope.documentProposalNotes = [];
        $scope.documentProposalFieldConstraints = [];
        $scope.documentProposalReaderFailures = [];
        // Resolves to the terms this source document negotiates. Started as
        // soon as phase one lands rather than when phase two begins: the
        // drafter spends real time reviewing the highlighted fields in between,
        // and that is the whole cost of the call.
        $scope.documentProposalConcepts = null;

        function startConceptListing(sourceText) {
          $scope.documentProposalConcepts = ApiService.listSourceConcepts({
            documentId: $scope.lease.id,
            text: sourceText
          }).then(function(concepts) {
            logAutopilotPayload('concepts', concepts);
            return concepts;
          });
        }

        $scope.documentProposalAppliedCount = null;
        $scope.documentProposalNoteCount = null;

        // The second pass takes minutes. It used to run behind a single
        // indeterminate message, which is indistinguishable from a hang. Both
        // signals below already exist — the phase broadcast and the
        // projectionReady event — so the wait can be narrated without asking
        // the pipeline for anything new.
        $scope.documentProposalStage = null;
        $scope.documentProposalElapsed = 0;
        // Subjects the reading stage steps through, derived from the field
        // constraints this run actually sent (document_proposal_topics.js).
        // Null between topics and once the list is spent, which is when the
        // general sentence takes over.
        $scope.documentProposalTopics = [];
        $scope.documentProposalTopic = null;

        var elapsedTimer = null;
        var readingStartedAtSecond = null;

        function stopElapsedClock() {
          if (elapsedTimer) {
            $interval.cancel(elapsedTimer);
            elapsedTimer = null;
          }
        }

        // Advanced off the elapsed clock rather than a timer of its own: one
        // tick already runs every second, and a second one could only drift
        // away from the number on screen.
        function updateNarrationTopic() {
          var topics = $scope.documentProposalTopics || [];
          if ($scope.documentProposalStage !== "reading" || !topics.length) {
            $scope.documentProposalTopic = null;
            return;
          }

          // Timed from where the reading stage began, not from the start of
          // the wait: applying the terms takes its own variable time, and
          // counting it would spend half the list before the pass had read a
          // word.
          if (readingStartedAtSecond === null) {
            readingStartedAtSecond = $scope.documentProposalElapsed;
          }

          $scope.documentProposalTopic = topicAtSecond(
            topics,
            $scope.documentProposalElapsed - readingStartedAtSecond
          );
        }

        function startElapsedClock() {
          stopElapsedClock();
          var startedAt = Date.now();
          $scope.documentProposalElapsed = 0;
          readingStartedAtSecond = null;
          $scope.documentProposalTopic = null;
          elapsedTimer = $interval(function() {
            $scope.documentProposalElapsed = Math.round(
              (Date.now() - startedAt) / 1000
            );
            updateNarrationTopic();
          }, 1000);
        }

        $scope.formatElapsed = function(seconds) {
          var total = Math.max(0, seconds || 0);
          var minutes = Math.floor(total / 60);
          var rest = total % 60;
          return minutes + ":" + (rest < 10 ? "0" : "") + rest;
        };

        $scope.$on("$destroy", stopElapsedClock);

        $scope.$on("documentProposals:phaseChanged", function(event, data) {
          $scope.documentProposalPhase = data && data.phase;
          if (data && typeof data.appliedCount === "number") {
            $scope.documentProposalAppliedCount = data.appliedCount;
          }
          if (data && typeof data.noteCount === "number") {
            $scope.documentProposalNoteCount = data.noteCount;
          }
          if (data && Array.isArray(data.readerFailures)) {
            $scope.documentProposalReaderFailures = data.readerFailures;
          }

          if ($scope.documentProposalPhase === "settling") {
            $scope.documentProposalStage = "applying";
            startElapsedClock();
          } else {
            stopElapsedClock();
          }
        });

        // The projection is built only after the document has settled, so this
        // event is the boundary between "applying the terms" and "reading the
        // lease" — the two halves of the wait, which take very different times.
        $scope.$on("documentProposals:projectionReady", function(event, data) {
          if ($scope.documentProposalPhase === "settling") {
            $scope.documentProposalStage = "reading";
            // The concept list is what the readers were actually sent, so it
            // narrates this document rather than what this form could hold.
            // The constraint table stays as the fallback for a run that has no
            // list.
            var fromConcepts = narrationTopicsForConcepts(data && data.concepts);
            if (fromConcepts.length > 0) {
              $scope.documentProposalTopics = fromConcepts;
            }
            updateNarrationTopic();
          }
        });

        // Renamed from `declineDocumentProposals`, which is what it was called
        // and not what it did. It clears this panel's own state and broadcasts
        // an event nothing listens for; every suggestion stays in the document
        // as a pending redline. A drafter who clicked "Decline suggested
        // changes" watched the panel disappear and reasonably concluded the
        // suggestions had gone with it.
        //
        // There is no bulk discard behind this, and inventing one here would be
        // guessing at semantics that belong with Version Control. So the panel
        // now closes and says so, and the suggestions are reviewed where they
        // live.
        $scope.dismissDocumentProposals = function() {
          $scope.documentProposalPhase = null;
          $scope.documentProposalStage = null;
          $scope.documentApplyPromise = null;
          $scope.documentProposalSourceText = null;
          $scope.documentProposalSourceDocument = null;
          $scope.documentProposalNotes = [];
          $scope.documentProposalFieldConstraints = [];
          $scope.documentProposalReaderFailures = [];
          $scope.documentProposalConcepts = null;
          stopElapsedClock();
        };

        // Angular expressions cannot carry a function literal, so the join
        // lives here rather than in the template.
        $scope.readerFailureSubjects = function() {
          return ($scope.documentProposalReaderFailures || [])
            .map(function(failure) {
              return failure && failure.subject;
            })
            .filter(Boolean)
            .join("; ");
        };

        // Import interface state
        $scope.importActiveTab = "upload"; // 'upload' or 'paste'
        $scope.importSelectedFiles = [];
        $scope.importProcessing = false;
        $scope.importExtractingText = false; // Track text extraction phase (file uploads only)
        $scope.importError = null;
        // Use an object to avoid scope inheritance issues with forms
        $scope.importData = {
          pastedText: ""
        };
        $scope.importMaxFileSize = 20 * 1024 * 1024; // 20MB in bytes

        // A run is in flight. Both phases count: the upload path reads the
        // document first and extracts terms second, and the input that fed
        // either one must not move underneath it.
        $scope.isImportBusy = function() {
          return $scope.importProcessing || $scope.importExtractingText;
        };

        $scope.importSupportedFileTypes = [
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/pdf",
          "image/jpeg",
          "image/jpg",
          "image/png"
        ];

        function getImportDealTermsConfig() {
          const form = ($scope.lease && $scope.lease.form) || {};

          return {
            instructions: form.customImportDealTermsInstructions,
            schema: form.customImportDealTermsFormat
          };
        }

        function collectFieldConstraints() {
          if (
            !FieldConstraintsService ||
            typeof FieldConstraintsService.collect !== "function"
          ) {
            return [];
          }

          return FieldConstraintsService.collect() || [];
        }

        // Expose pending changes to template
        $scope.getPendingChanges = function() {
          return DealTermsImportService.pendingChanges || [];
        };

        $scope.getAdvisoryChanges = function() {
          return DealTermsImportService.advisoryChanges || [];
        };

        $scope.getImportGeneratedNotesCount = function() {
          return (DealTermsImportService.extractedNotes || []).length;
        };

        $scope.getImportGeneratedNotes = function() {
          return DealTermsImportService.extractedNotes || [];
        };

        function decodeNoteText(text) {
          return String(text || "")
            .replace(/&#39;/g, "'")
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, "&");
        }

        // The notes section is closed on arrival and stays closed until asked
        // for. A real LOI extracts dozens of them, and the panel opening onto
        // that list is what the drafter meets first, ahead of the fields the
        // step is actually about.
        //
        // Not persisted: this is per-import state, and an import is a single
        // sitting. Re-running the extraction should start closed again.
        $scope.importNotesExpanded = false;

        // Empty unless the extraction produced one the server was willing to
        // pass on. The template falls back to describing what a note is.
        $scope.getImportNotesSummary = function() {
          return DealTermsImportService.extractedNotesSummary || "";
        };

        $scope.toggleImportNotes = function() {
          $scope.importNotesExpanded = !$scope.importNotesExpanded;
        };

        // Notes clamp to two lines by default. Tracked by index because the
        // notes have no stable identity of their own.
        $scope.expandedImportNotes = {};

        $scope.isImportNoteExpanded = function(index) {
          return Boolean($scope.expandedImportNotes[index]);
        };

        $scope.toggleImportNote = function(index) {
          $scope.expandedImportNotes[index] = !$scope.expandedImportNotes[index];
        };

        // Only offer the toggle when there is something to reveal. The cutoff
        // approximates two lines in the 450px sidebar; a note under it renders
        // whole and gets no control.
        var NOTE_CLAMP_CHARACTERS = 110;

        $scope.importNoteIsLong = function(note) {
          return $scope.getImportNoteDisplayText(note).length > NOTE_CLAMP_CHARACTERS;
        };

        $scope.getImportNoteDisplayText = function(note) {
          if (!note || !note.text) return "";
          return decodeNoteText(note.text)
            .replace(/^Imported Deal Terms Notes?:\s*/i, "")
            .replace(/<br\s*\/?>/gi, " ")
            .replace(/\d+\.\s*/g, "")
            .trim();
        };

        $scope.navigateToImportNote = function(note) {
          const target = noteNavigationTarget(note);
          if (!target || !window.leftSideEditorSearch) return;

          const fullPath = target.startsWith("lease.") ? target : "lease." + target;
          window.leftSideEditorSearch.searchByVariableName(fullPath, 0);
        };

        $scope.importNoteHasTarget = function(note) {
          return !!noteNavigationTarget(note);
        };

        // Get the full path for label (e.g., 'lease.tenantInfo.name')
        $scope.getLabelPath = function(change) {
          if (!change || !change.path) return "";
          // If path already starts with 'lease.', return as is
          if (change.path.startsWith("lease.")) {
            return change.path;
          }
          // Otherwise, prepend 'lease.'
          return "lease." + change.path;
        };

        // Get field type from DOM
        $scope.getFieldType = function(change) {
          if (!change || !change.path) return "text";

          try {
            var fullPath = $scope.getLabelPath(change);

            // Find the editor-control element
            var controlElement = document.querySelector(
              'editor-control[for="' + fullPath + '"]'
            );

            if (!controlElement) {
              return "text"; // Default to text
            }

            // Check for checkbox
            if (controlElement.querySelector("md-checkbox")) {
              return "checkbox";
            }

            // Check for radio buttons
            if (controlElement.querySelector("md-radio-group")) {
              return "radio";
            }

            // Check for select/dropdown
            if (controlElement.querySelector("select")) {
              return "select";
            }

            // Check for textarea
            if (controlElement.querySelector("textarea")) {
              return "textarea";
            }

            // Check for text input
            var input = controlElement.querySelector("input");
            if (input) {
              var inputType = input.getAttribute("type");
              if (inputType === "text" || !inputType) {
                return "text";
              }
              return inputType; // number, date, etc.
            }

            return "text"; // Default
          } catch (error) {
            console.warn(
              "Error getting field type for path:",
              change.path,
              error
            );
            return "text";
          }
        };

        // Format value for display based on field type
        $scope.formatValue = function(value, change) {
          if (value === null || value === undefined || value === "") {
            return "(empty)";
          }

          // Get field type if change is provided
          var fieldType = change ? $scope.getFieldType(change) : "text";

          // Handle checkbox - show Yes/No instead of true/false
          if (fieldType === "checkbox") {
            if (
              value === true ||
              value === "true" ||
              value === 1 ||
              value === "1"
            ) {
              return "Yes";
            }
            if (
              value === false ||
              value === "false" ||
              value === 0 ||
              value === "0"
            ) {
              return "No";
            }
          }

          // Handle select/dropdown - extract number from "number:X" format
          if (fieldType === "select") {
            var valueStr = String(value);
            // Check if value is in "number:X" format
            var numberMatch = valueStr.match(/^number:(\d+(?:\.\d+)?)$/);
            if (numberMatch) {
              return numberMatch[1]; // Return just the number
            }
            // If it's already a number, return it as string
            if (typeof value === "number") {
              return String(value);
            }
          }

          // Handle objects
          if (typeof value === "object") {
            return JSON.stringify(value);
          }

          return String(value);
        };

        // Listen for import state changes
        $rootScope.$on("dealTermsImport:started", function() {
          $scope.isDealTermsImportActive =
            DealTermsImportService.isImportActive;
          if (!$rootScope.$$phase) {
            $scope.$apply();
          }
        });

        $rootScope.$on("dealTermsImport:finished", function() {
          $scope.isDealTermsImportActive = false;
          // This fires from $broadcast inside finishDealTermsImport, which runs
          // from an ng-click already wrapped in $apply, so an unconditional
          // $apply() threw "$apply already in progress" on every finish. It was
          // non-fatal, but it filled the console during the second pass and
          // buried the error that actually mattered.
          if (!$rootScope.$$phase && !$scope.$$phase) {
            $scope.$apply();
          }
        });

        // role="tablist" promises arrow-key movement and a roving tabindex.
        // Declaring the role without them tells a screen-reader user this is a
        // tablist and then behaves like two unrelated buttons.
        var IMPORT_TABS = ["upload", "paste"];

        $scope.onImportTabKeydown = function(event, tab) {
          var index = IMPORT_TABS.indexOf(tab);
          var next = null;

          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            next = IMPORT_TABS[(index + 1) % IMPORT_TABS.length];
          } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
            next = IMPORT_TABS[(index + IMPORT_TABS.length - 1) % IMPORT_TABS.length];
          } else if (event.key === "Home") {
            next = IMPORT_TABS[0];
          } else if (event.key === "End") {
            next = IMPORT_TABS[IMPORT_TABS.length - 1];
          }

          if (!next) return;

          event.preventDefault();
          $scope.setImportActiveTab(next);
          var target = document.getElementById("dt-tab-" + next);
          if (target) target.focus();
        };

        $scope.setImportActiveTab = function(tab) {
          $scope.importActiveTab = tab;
          $scope.importError = null;

          // Ensure scope is updated when switching tabs
          if (!$scope.$$phase && !$scope.$root.$$phase) {
            $scope.$apply();
          }
        };

        $scope.uploadImportFiles = function(files) {
          $scope.importError = null;

          if (!files) {
            return;
          }

          // Handle both single file and array of files
          const fileArray = Array.isArray(files) ? files : [files];

          if (fileArray.length === 0) {
            return;
          }

          // Validate file sizes and types
          const validFiles = [...$scope.importSelectedFiles]; // Keep existing files
          const errors = [];

          for (let i = 0; i < fileArray.length; i++) {
            const file = fileArray[i];

            // Check if file already exists
            const fileExists = $scope.importSelectedFiles.some(
              f => f.name === file.name && f.size === file.size
            );
            if (fileExists) {
              continue; // Skip duplicate files
            }

            // Check file size
            if (file.size > $scope.importMaxFileSize) {
              errors.push(`"${file.name}" exceeds 20MB limit`);
              continue;
            }

            // Check file type
            const isValidType =
              $scope.importSupportedFileTypes.includes(file.type) ||
              file.name.match(/\.(doc|docx|pdf|jpg|jpeg|png)$/i);
            if (!isValidType) {
              errors.push(`"${file.name}" format not supported`);
              continue;
            }

            validFiles.push(file);
          }

          if (errors.length > 0) {
            $scope.importError = errors.join(". ") + ".";
          }

          $scope.importSelectedFiles = validFiles;
        };

        $scope.removeImportFile = function(index) {
          $scope.importSelectedFiles.splice(index, 1);
        };

        $scope.clearImportFiles = function() {
          $scope.importSelectedFiles = [];
          $scope.importError = null;
          // Clear the file input element
          var fileInput = document.getElementById("deal-terms-files-inline");
          if (fileInput) {
            fileInput.value = "";
          }
        };

        $scope.clearImportText = function() {
          $scope.importData.pastedText = "";
          $scope.importError = null;
        };

        $scope.analyzeImportDocument = function() {
          $scope.importError = null;

          // Validate input
          if (
            $scope.importActiveTab === "upload" &&
            $scope.importSelectedFiles.length === 0
          ) {
            $scope.importError = "Please select at least one file to analyze.";
            return;
          }

          if ($scope.importActiveTab === "paste") {
            // Ensure we have a string value
            const pastedText = ($scope.importData.pastedText || "").trim();
            if (!pastedText) {
              $scope.importError = "Please paste or enter document content.";
              return;
            }
          }

          if (!$scope.lease || !$scope.lease.id) {
            $scope.importError =
              "Lease ID is missing. Please refresh the page and try again.";
            return;
          }

          if (!AiServicesConsentService.hasAcceptedAiServicesConsent()) {
            AiServicesConsentService.ensureAiServicesConsent()
              .then(function() {
                $scope.analyzeImportDocument();
              })
              .catch(function() {
                $scope.importError =
                  "AI Services consent is required to analyze deal terms.";
              });
            return;
          }

          // If input is text, skip Step 1 and go directly to Step 2
          if ($scope.importActiveTab === "paste") {
            $scope.importProcessing = true;
            const pastedText = ($scope.importData.pastedText || "").trim();
            const importConfig = getImportDealTermsConfig();
            const fieldConstraints = collectFieldConstraints();

            // Alongside the extraction, not after it. Both calls read this same
            // text and neither needs the other's answer, so running them in
            // series only delayed the list. The upload path has always started
            // it here — the moment the text exists — and this brings the paste
            // path into line.
            startConceptListing(pastedText);

            // Step 2: Extract structured deal terms directly from the text
            ApiService.extractDealTerms({
              documentId: $scope.lease.id,
              text: pastedText,
              instructions: importConfig.instructions,
              schema: importConfig.schema,
              fieldConstraints: fieldConstraints
            })
              .then(function(extractResponse) {
                $scope.importProcessing = false;
                logAutopilotPayload('dealTerms', extractResponse && extractResponse.data);

                if (extractResponse && extractResponse.data.success) {
                  const normalizedExtraction = DealTermsImportService.normalizeExtractionResponse(
                    extractResponse
                  );
                  $scope.documentProposalSourceText = pastedText;
                  $scope.documentProposalSourceDocument = 'Pasted text';
                  // Stashed here, not recollected at phase two: by then the
                  // form has re-rendered with the imported values and a fresh
                  // collect would describe a different document.
                  $scope.documentProposalFieldConstraints = fieldConstraints;
                  $scope.documentProposalNotes = normalizedExtraction.extractedNotes.slice();

                  // Initialize the import service with extracted fields
                  $scope.documentApplyPromise = DealTermsImportService.initializeImport(
                    normalizedExtraction.extractedTerms,
                    $scope.lease,
                    {
                      fieldConstraints: fieldConstraints,
                      extractedNotes: normalizedExtraction.extractedNotes,
                      extractedNotesSummary:
                        normalizedExtraction.extractedNotesSummary
                    }
                  );

                  return $scope.documentApplyPromise.then(function() {
                    // Ensure Angular detects the changes
                    $scope.safeApply(() => {
                      // Update the lease reference
                      $rootScope.lease = $scope.lease;

                      // Update import state
                      $scope.isDealTermsImportActive =
                        DealTermsImportService.isImportActive;
                    });

                    // Trigger an update to save the changes
                    $rootScope.$broadcast("dealTermsImport:applyChange", {
                      change: null // Signal to save all changes
                    });
                  });
                } else if (extractResponse) {
                  $scope.importError =
                    extractResponse.data.error ||
                    "Failed to extract deal terms. Please try again.";
                }
              })
              .catch(function(error) {
                $scope.importProcessing = false;

                if (error.data && error.data.error) {
                  $scope.importError = error.data.error;
                } else if (error.status === 0) {
                  $scope.importError =
                    "Request timed out. The analysis may take several minutes. Please try again.";
                } else {
                  $scope.importError =
                    "An error occurred while processing the document. Please try again.";
                }

                console.error("Error processing deal terms:", error);
              });
          } else {
            // For file uploads: Step 1 - Extract text from documents, then Step 2 - Extract deal terms
            $scope.importExtractingText = true;
            const importConfig = getImportDealTermsConfig();
            const fieldConstraints = collectFieldConstraints();
            
            const requestData = {
              documentId: $scope.lease.id,
              files: $scope.importSelectedFiles,
              instructions: importConfig.instructions,
              schema: importConfig.schema
            };

            // Step 1: Extract text from documents
            ApiService.extractDocumentText(requestData)
              .then(function(textResponse) {
                if (!textResponse.data.success) {
                  $scope.importExtractingText = false;
                  $scope.importProcessing = false;
                  $scope.importError =
                    textResponse.data.error ||
                    "Failed to extract text from documents. Please try again.";
                  return;
                }

                const extractedText = textResponse.data.text;
                $scope.documentProposalSourceText = extractedText;
                startConceptListing(extractedText);
                $scope.documentProposalSourceDocument = $scope.importSelectedFiles
                  .map(file => file.name)
                  .join(', ');
                // Same reasoning as the paste path: captured against the form
                // as phase one saw it, not as phase two would re-read it.
                $scope.documentProposalFieldConstraints = fieldConstraints;

                // Transition from text extraction to deal terms extraction
                $scope.importExtractingText = false;
                $scope.importProcessing = true;

                // Step 2: Extract structured deal terms from the text
                return ApiService.extractDealTerms({
                  documentId: $scope.lease.id,
                  text: extractedText,
                  instructions: importConfig.instructions,
                  schema: importConfig.schema,
                  fieldConstraints: fieldConstraints
                });
              })
              .then(function(extractResponse) {
                $scope.importProcessing = false;
                logAutopilotPayload('dealTerms', extractResponse && extractResponse.data);

                if (extractResponse && extractResponse.data.success) {
                  const normalizedExtraction = DealTermsImportService.normalizeExtractionResponse(
                    extractResponse
                  );
                  $scope.documentProposalNotes = normalizedExtraction.extractedNotes.slice();

                  // Initialize the import service with extracted fields (returns a Promise)
                  $scope.documentApplyPromise = DealTermsImportService.initializeImport(
                    normalizedExtraction.extractedTerms,
                    $scope.lease,
                    {
                      fieldConstraints: fieldConstraints,
                      extractedNotes: normalizedExtraction.extractedNotes,
                      extractedNotesSummary:
                        normalizedExtraction.extractedNotesSummary
                    }
                  );
                  $scope.documentApplyPromise
                    .then(function() {
                      // Ensure Angular detects the changes
                      $scope.safeApply(() => {
                        // Update the lease reference
                        $rootScope.lease = $scope.lease;

                        // Update import state
                        $scope.isDealTermsImportActive =
                          DealTermsImportService.isImportActive;
                      });

                      // Trigger an update to save the changes
                      $rootScope.$broadcast("dealTermsImport:applyChange", {
                        change: null // Signal to save all changes
                      });

                      // Ensure Deal Terms panel is visible
                      $rootScope.leftPane = "deal-terms";
                    })
                    .catch(function(error) {
                      console.error(
                        "Error initializing deal terms import:",
                        error
                      );
                      $scope.importError =
                        "An error occurred while processing the imported deal terms. Please try again.";
                      $scope.safeApply();
                    });
                } else if (extractResponse) {
                  $scope.importError =
                    extractResponse.data.error ||
                    "Failed to extract deal terms. Please try again.";
                }
              })
              .catch(function(error) {
                $scope.importExtractingText = false;
                $scope.importProcessing = false;

                if (error.data && error.data.error) {
                  $scope.importError = error.data.error;
                } else if (error.status === 0) {
                  $scope.importError =
                    "Request timed out. The analysis may take several minutes. Please try again.";
                } else {
                  $scope.importError =
                    "An error occurred while processing the document. Please try again.";
                }

                console.error("Error processing deal terms:", error);
              });
          }
        };

        $scope.finishDealTermsImport = function() {
          const documentProposalContext = {
            sourceText: $scope.documentProposalSourceText,
            sourceDocument: $scope.documentProposalSourceDocument,
            notes: $scope.documentProposalNotes.slice(),
            // Captured when the document was analyzed rather than recollected
            // here: by now the form has been re-rendered with the imported
            // values, so a fresh collect would describe a different document
            // than the one phase one extracted against.
            fieldConstraints: ($scope.documentProposalFieldConstraints || []).slice(),
            // A promise, not a list: the drafter can click through before it
            // lands. Phase two waits for it behind the settle, which takes
            // longer anyway.
            concepts: $scope.documentProposalConcepts || Promise.resolve([])
          };
          // Off the same constraints the request carries, so the panel can
          // only name ground the pass was actually given.
          $scope.documentProposalTopics = narrationTopicsFor(
            documentProposalContext.fieldConstraints
          );
          // Trigger update to save all changes before finishing
          $rootScope.$broadcast("dealTermsImport:applyChange", {
            change: null // Signal to save all changes
          });
          DealTermsImportService.finishImport();
          $scope.isDealTermsImportActive = false;

          const appScope = $rootScope.findAppScope();
          if (
            appScope &&
            typeof appScope.prepareDocumentChangeProposals === "function"
          ) {
            $scope.documentProposalPhase = "settling";
            // Same move phase one makes when it starts (initializeImport):
            // the panel is usually scrolled down where the drafter left the
            // field review, and phase two's message and spinner are at the top.
            DealTermsImportService.scrollToTop();
            appScope
              .prepareDocumentChangeProposals(
                $scope.documentApplyPromise || Promise.resolve(),
                documentProposalContext
              )
              .catch(function(error) {
                $scope.documentProposalPhase = null;
                $scope.importError =
                  "Deal terms were applied, but document changes could not be proposed. Please try again.";
                console.error("Unable to prepare document change proposals:", error);
                $scope.safeApply();
              });
          }

          // Clear files and text
          $scope.clearImportFiles();
          $scope.clearImportText();

          // Broadcast event to remove all accept/reject buttons
          $rootScope.$broadcast("dealTermsImport:finished");
        };

        $scope.rejectAllChanges = function() {
          if (
            confirm(
              "Are you sure you want to reject all changes? This will revert all imported values back to their original state."
            )
          ) {
            // rejectAllChanges handles reverting values and cleaning up service state
            // After it completes, call finishDealTermsImport to clear UI state (files, textarea, etc.)
            DealTermsImportService.rejectAllChanges()
              .then(function() {
                // Clear files and text after rejection completes
                $scope.clearImportFiles();
                $scope.clearImportText();

                // Update local state
                $scope.isDealTermsImportActive = false;
                $scope.documentApplyPromise = null;
              })
              .catch(function(error) {
                console.error("Error rejecting all changes:", error);
              });
          }
        };
      }
    };
  }
]);
